import { map, Observable, of, shareReplay, tap } from 'rxjs';
import { RosterPublic } from '../models/roster-public';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Roster } from '../models/roster';
import { Exclusivity } from '../models/exclusivity';
import { MediaKitResponse } from '../models/media-kit-response';

export interface CalendarReconcileAction {
  action: 'insert' | 'update' | 'delete';
  taskId: string;
  summary?: string | null;
  date?: string | null;
  error?: string | null;
}

export interface CalendarReconcileResult {
  rosterId: string;
  email: string | null;
  calendarEventsEnabled: boolean;
  dryRun: boolean;
  actions: CalendarReconcileAction[];
  failed: number;
}

@Injectable({ providedIn: 'root' })
export class RosterService {

  rosterCache: Roster[] = [];
  publicRosterCache: RosterPublic[] = [];
  private rostersRequest$: Observable<Roster[]> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * GET rosters/{id}/exclusivities - the roster's own exclusivities merged with the exclusivities
   * of the roster's campaigns. Campaign-level ones carry `campaignName`; the roster's own do not.
   */
  getRosterExclusivities(id: string): Observable<Exclusivity[]> {
    const url = `${environment.apiUrl}rosters/${id}/exclusivities`;
    return this.http.get<Exclusivity[]>(url);
  }

  getRoster(id: string): Observable<Roster> {
    const cacheIndex = this.rosterCache.findIndex(c => c.id === id);
    if (cacheIndex >= 0) {
      return of(this.rosterCache[cacheIndex]);
    }

    // Always fetch all rosters and return the requested one from the cache/list.
    return this.getRosters().pipe(
      map(list => {
        const found = list.find(r => r.id === id);
        if (!found) {
          throw new Error(`Roster with id ${id} not found`);
        }
        return found;
      })
    );
  }

  /**
   * Fetch public roster brief entries. Without ids, returns featured creators for the
   * homepage/marketing carousel (result is cached). With ids, returns those specific
   * rosters without authentication.
   */
  getPublicRosters(ids?: string[]): Observable<RosterPublic[]> {
    if (!ids || ids.length === 0) {
      if (this.publicRosterCache.length > 0) {
        return of(this.publicRosterCache);
      }
      const url = `${environment.apiUrl}roster/public`;
      return this.http.get<RosterPublic[]>(url).pipe(
        tap(list => this.publicRosterCache = list || [])
      );
    }

    const url = `${environment.apiUrl}roster/public?ids=${ids.join(',')}`;
    return this.http.get<RosterPublic[]>(url);
  }

  getRosters(): Observable<Roster[]> {
    if (this.rosterCache.length > 0) {
      return of(this.rosterCache);
    }
    if (!this.rostersRequest$) {
      const url = `${environment.apiUrl}rosters`;
      this.rostersRequest$ = this.http.get<Roster[]>(url).pipe(
        tap((result) => { this.rosterCache = result; }),
        shareReplay(1)
      );
    }
    return this.rostersRequest$;
  }

  upsertRoster(roster: Roster): Observable<Roster> {
    const url = `${environment.apiUrl}rosters`;
    return this.http.post<Roster>(url, roster);
  }

  /**
   * Patch a single field on a roster record using LWW (last-writer-wins).
   * Sends PATCH to rosters/{id}/field with body { field, value }.
   * If the server returns the updated Roster, the local cache will be updated.
   */
  patchRosterField(id: string, field: string, value: any): Observable<Roster> {
    const url = `${environment.apiUrl}rosters/${id}/field`;
    const body = { field, value };
    return this.http.patch<Roster>(url, body).pipe(
      tap((updated) => {
        // If we have the roster in cache, replace it with the updated object (if returned)
        const idx = this.rosterCache.findIndex(r => r.id === id);
        if (idx >= 0 && updated) {
          this.rosterCache[idx] = updated;
        }
      })
    );
  }

  /**
   * Admin only. Brings the creator's Google Calendar in line with their tasks: adds / updates the
   * events of their creator-facing tasks when Roster.enableCalendarEvents is on, and removes all
   * of them when it is off. Idempotent, so it doubles as a "Sync now" repair.
   */
  reconcileCalendar(id: string, dryRun = false): Observable<CalendarReconcileResult> {
    const url = `${environment.apiUrl}calendar/reconcile/${id}${dryRun ? '?dryRun=true' : ''}`;
    return this.http.post<CalendarReconcileResult>(url, null);
  }

  /**
   * Patch a single field on an exclusivity within a roster using LWW.
   * Sends PATCH to rosters/{id}/exclusivities/{exclusivityId}/field with body { field, value }.
   * If the server returns the updated Roster, the local cache will be updated.
   */
  patchExclusivityField(id: string, exclusivityId: string, field: string, value: any): Observable<any> {
    const url = `${environment.apiUrl}rosters/${id}/exclusivities/${exclusivityId}`;
    const body = { field, value };
    return this.http.patch<any>(url, body).pipe(
      tap((resp) => {
        // Server returns the updated Exclusivity object; merge into rosterCache
        if (!resp || !resp.id) return;
        const idx = this.rosterCache.findIndex(r => r.id === id);
        if (idx < 0) return;
        const roster = this.rosterCache[idx];
        roster.exclusivities = roster.exclusivities || [];
        const exIdx = roster.exclusivities.findIndex((e: any) => e.id === resp.id);
        if (exIdx >= 0) roster.exclusivities[exIdx] = resp;
        else roster.exclusivities.push(resp);
        // ensure we replace the cached roster reference so subscribers see an update
        this.rosterCache[idx] = { ...roster } as any;
      })
    );
  }

  /**
   * Add a new exclusivity to a roster.
   * POSTs to rosters/{id}/exclusivities with the exclusivity object.
   * If server returns updated Roster, update local cache.
   */
  addExclusivity(rosterId: string, exclusivity: any): Observable<Roster> {
    const url = `${environment.apiUrl}rosters/${rosterId}/exclusivities`;
    return this.http.post<Roster>(url, exclusivity).pipe(
      tap((updated) => {
        const idx = this.rosterCache.findIndex(r => r.id === rosterId);
        if (idx >= 0 && updated) {
          this.rosterCache[idx] = updated;
        }
      })
    );
  }

  /**
   * Delete an exclusivity from a roster.
   * Sends DELETE to rosters/{id}/exclusivities/{exclusivityId}.
   * If the server returns an updated Roster, update the cache. If the server returns no content,
   * remove the exclusivity locally from the cached roster so the client reflects the deletion.
   */
  deleteExclusivity(rosterId: string, exclusivityId: string): Observable<void> {
    const url = `${environment.apiUrl}rosters/${rosterId}/exclusivities/${exclusivityId}`;
    return this.http.delete<void>(url).pipe(
      tap(() => {
        // On success (204/200), remove the exclusivity from the cached roster if present
        const idx = this.rosterCache.findIndex(r => r.id === rosterId);
        if (idx < 0) return;
        const roster = this.rosterCache[idx];
        roster.exclusivities = roster.exclusivities || [];
        const exIdx = roster.exclusivities.findIndex((e: any) => e.id === exclusivityId);
        if (exIdx >= 0) roster.exclusivities.splice(exIdx, 1);
        // replace the cached roster reference so subscribers see a change
        this.rosterCache[idx] = { ...roster } as any;
      })
    );
  }

  /**
   * Fetch audience demographics for all platforms linked to a roster.
   * Returns an array of per-platform results, each carrying its own lastRefresh timestamp.
   * Pass forceRefresh=true to bypass the server-side 7-day cache.
   */
  getDemographics(rosterId: string, forceRefresh = false): Observable<any[]> {
    let url = `${environment.apiUrl}rosters/${rosterId}/demographics`;
    if (forceRefresh) url += '?forceRefresh=true';
    return this.http.get<any[]>(url);
  }

  /**
   * Fetch top 4 videos by view count for TikTok accounts linked to a roster.
   * Returns a TopVideosResponse with lastRefresh timestamp and data array.
   * Pass forceRefresh=true to bypass the server-side 7-day cache.
   */
  getTopVideos(rosterId: string, forceRefresh = false): Observable<any> {
    let url = `${environment.apiUrl}rosters/${rosterId}/top-videos`;
    if (forceRefresh) url += '?forceRefresh=true';
    return this.http.get<any>(url);
  }

  getMediaKit(id: string, forceRefresh = false): Observable<MediaKitResponse> {
    let url = `${environment.apiUrl}mediakit/${id}`;
    if (forceRefresh) url += '?forceRefresh=true';
    return this.http.get<MediaKitResponse>(url);
  }

  getPastPartnerships(rosterId: string): Observable<string[]> {
    return this.http.get<string[]>(`${environment.apiUrl}roster/${rosterId}/past-partnerships`);
  }

}
